import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import LanguageSwitcher from './LanguageSwitcher';

interface UploadedFile {
  id: string;
  name: string;
  originalName: string;
  size: number;
  uploadedAt: string;
  url: string;
  isAnonymous: boolean;
}

interface UploadResponse {
  file?: UploadedFile;
  message?: string;
  sentTo?: string;
  stored?: boolean;
  metadata?: InvoiceMetadata;
  temporaryXml?: {
    id: string;
    downloadUrl: string;
  };
}

interface InvoiceMetadata {
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  vendorName?: string;
  customerName?: string;
  totalAmount?: number;
  taxAmount?: number;
  currency?: string;
  paymentTerms?: string;
  confidence?: number;
}

const AnonymousUpload: React.FC = () => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [storedResult, setStoredResult] = useState<boolean | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisMetadata, setAnalysisMetadata] = useState<InvoiceMetadata | null>(null);
  const [temporaryXmlId, setTemporaryXmlId] = useState<string | null>(null);
  const [temporaryXmlUrl, setTemporaryXmlUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [processingConsent, setProcessingConsent] = useState(false);
  const progressTimerRef = useRef<number | null>(null);

  const cleanupTemporaryXml = (tempId?: string | null, useBeacon = false) => {
    if (!tempId) return;

    const cleanupUrl = API_ENDPOINTS.public.tempXmlCleanup(tempId);
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(cleanupUrl, new Blob([], { type: 'text/plain' }));
      return;
    }

    void axios.post(cleanupUrl).catch((cleanupError) => {
      console.warn('Failed to cleanup temporary XML:', cleanupError);
    });
  };

  useEffect(() => {
    if (!temporaryXmlId) return;

    const handleSessionEnd = () => {
      cleanupTemporaryXml(temporaryXmlId, true);
    };

    window.addEventListener('beforeunload', handleSessionEnd);
    window.addEventListener('pagehide', handleSessionEnd);

    return () => {
      window.removeEventListener('beforeunload', handleSessionEnd);
      window.removeEventListener('pagehide', handleSessionEnd);
    };
  }, [temporaryXmlId]);

  const handleDownloadTemporaryXml = async () => {
    if (!temporaryXmlId) return;

    try {
      const response = await axios.get<Blob>(API_ENDPOINTS.public.tempXmlDownload(temporaryXmlId), {
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(response.data);
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = 'analysis.xml';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError: any) {
      console.error('Failed to download temporary XML:', downloadError);
      setError(downloadError?.response?.data?.error || 'Failed to download XML file. Please retry upload.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError('');
    } else {
      alert(t('dashboard.pdfOnly'));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    if (!senderEmail) {
      setError(t('anonymousUpload.errors.emailRequired'));
      return;
    }

    cleanupTemporaryXml(temporaryXmlId);
    setTemporaryXmlId(null);
    setTemporaryXmlUrl(null);
    setAnalysisMetadata(null);
    setUploadedFile(null);
    setStoredResult(null);
    setUploadMessage('');
    setUploadProgress(8);

    progressTimerRef.current = window.setInterval(() => {
      setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 6));
    }, 450);

    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('senderEmail', senderEmail);
    formData.append('processingConsent', String(processingConsent));

    try {
      const currentEmail = senderEmail;
      const response = await axios.post<UploadResponse>(API_ENDPOINTS.public.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploadedFile(response.data.file || null);
      setStoredResult(response.data.stored ?? Boolean(response.data.file));
      setUploadMessage(response.data.message || t('anonymousUpload.uploadSuccessMessage'));
      setConfirmationEmail(response.data.sentTo || currentEmail);
      setAnalysisMetadata(response.data.metadata || null);
      if (response.data.temporaryXml?.id) {
        setTemporaryXmlId(response.data.temporaryXml.id);
        setTemporaryXmlUrl(
          response.data.temporaryXml.downloadUrl.startsWith('http')
            ? response.data.temporaryXml.downloadUrl
            : `${window.location.origin}${response.data.temporaryXml.downloadUrl}`
        );
      }
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      console.error('Upload failed:', error);
      setError(error.response?.data?.error || t('dashboard.uploadError'));
    } finally {
      if (progressTimerRef.current) {
        window.clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setUploadProgress(100);
      setUploading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-100 via-white to-cyan-100">
      <div className="pointer-events-none absolute -left-20 top-16 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-40 h-80 w-80 rounded-full bg-cyan-300/30 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-emerald-200/30 blur-3xl" />

      {/* Header */}
      <nav className="relative z-10 border-b border-slate-200/70 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex min-h-16 flex-col justify-center gap-2 py-2 sm:h-16 sm:flex-row sm:items-center sm:justify-between sm:py-0">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 shadow-sm">
                  <span className="text-white font-bold text-sm">A</span>
                </div>
                <h1 className="text-xl font-semibold text-slate-800">Alenis</h1>
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-4">
              <LanguageSwitcher />
              <Link
                to="/login"
                className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-200 sm:px-4 sm:text-base"
              >
                {t('navigation.login')}
              </Link>
              <Link
                to="/register"
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:px-4 sm:text-base"
              >
                {t('navigation.register')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 mx-auto grid max-w-6xl gap-8 px-4 py-8 sm:px-6 sm:py-12 lg:grid-cols-[1.1fr_1fr] lg:py-16">
        <section className="rounded-3xl border border-slate-200/70 bg-white/75 p-8 shadow-xl backdrop-blur-sm lg:p-10">
          <div className="mb-8">
            <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
              OCR + AI
            </span>
            <h1 className="mt-4 text-4xl font-bold leading-tight text-slate-900 sm:text-5xl">
              {t('anonymousUpload.title')}
            </h1>
            <p className="mt-4 text-lg text-slate-600 sm:text-xl">
              {t('anonymousUpload.subtitle')}
            </p>
          </div>

          <div className="grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="font-semibold text-slate-800">PDF</p>
              <p>Single-file upload</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="font-semibold text-slate-800">Secure</p>
              <p>Private by default</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="font-semibold text-slate-800">Fast</p>
              <p>Instant processing start</p>
            </div>
          </div>
        </section>

        {/* Upload Section */}
        <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl lg:p-9">
          <h2 className="mb-6 text-2xl font-semibold text-slate-800">
            {t('dashboard.uploadPdf')}
          </h2>
          
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label
                htmlFor="file-upload"
                className="group block cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center transition hover:border-blue-400 hover:bg-blue-50"
              >
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-slate-800">Choose PDF to upload</p>
                <p className="mt-1 text-xs text-slate-500">
                  {selectedFile ? selectedFile.name : 'Click here to select your document'}
                </p>
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="sr-only"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('anonymousUpload.senderEmailLabel')}</label>
              <input
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('anonymousUpload.senderEmailPlaceholder')}
              />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-start space-x-2">
              <input
                id="processing-consent"
                type="checkbox"
                checked={processingConsent}
                onChange={(e) => setProcessingConsent(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="processing-consent" className="text-sm text-slate-700">{t('anonymousUpload.processingConsentLabel')}</label>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {t('anonymousUpload.liabilityNotice')}
              </p>
            </div>
            
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="w-full rounded-xl bg-blue-600 px-6 py-3 text-lg font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploading ? t('dashboard.uploading') : t('common.upload')}
            </button>

            {uploading && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="mb-2 flex items-center justify-between text-sm font-medium text-blue-800">
                  <span>{t('anonymousUpload.analysisInProgress')}</span>
                  <span>{Math.min(uploadProgress, 100)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-blue-100">
                  <div
                    className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${Math.min(uploadProgress, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Upload Success */}
        {(uploadedFile || uploadMessage) && (
          <section className="lg:col-span-2 rounded-3xl border border-emerald-200 bg-white p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mb-2 text-2xl font-semibold text-gray-900">
                {t('dashboard.uploadSuccess')}
              </h3>
              <p className="mb-6 text-gray-600">
                {uploadMessage || t('anonymousUpload.uploadSuccessMessage')}
              </p>
              {confirmationEmail && storedResult && (
                <p className="text-sm font-medium text-emerald-700">
                  {t('anonymousUpload.responseSentTo', { email: confirmationEmail })}
                </p>
              )}
            </div>

            {uploadedFile && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center space-x-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
                    <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h4 className="truncate font-medium text-slate-800">{uploadedFile.originalName}</h4>
                    <p className="text-sm text-slate-500">
                      {formatFileSize(uploadedFile.size)} • {t('dashboard.uploadDate')}: {new Date(uploadedFile.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="self-start rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700 sm:self-center">
                  Private upload
                </span>
              </div>
              <p className="mt-4 text-sm text-slate-600">
                {t('anonymousUpload.privateUploadNotice')}
              </p>
            </div>
            )}

            {!storedResult && (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
                {t('anonymousUpload.notStoredNotice')}
              </div>
            )}

            {analysisMetadata && (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
                <h4 className="mb-4 text-lg font-semibold text-slate-900">{t('anonymousUpload.analysisResultTitle')}</h4>
                <div className="grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                  <p><span className="font-medium">{t('anonymousUpload.fields.invoiceNumber')}:</span> {analysisMetadata.invoiceNumber || '-'}</p>
                  <p><span className="font-medium">{t('anonymousUpload.fields.invoiceDate')}:</span> {analysisMetadata.invoiceDate || '-'}</p>
                  <p><span className="font-medium">{t('anonymousUpload.fields.dueDate')}:</span> {analysisMetadata.dueDate || '-'}</p>
                  <p><span className="font-medium">{t('anonymousUpload.fields.vendor')}:</span> {analysisMetadata.vendorName || '-'}</p>
                  <p><span className="font-medium">{t('anonymousUpload.fields.customer')}:</span> {analysisMetadata.customerName || '-'}</p>
                  <p>
                    <span className="font-medium">{t('anonymousUpload.fields.totalAmount')}:</span>{' '}
                    {analysisMetadata.totalAmount != null
                      ? `${analysisMetadata.totalAmount} ${analysisMetadata.currency || ''}`
                      : '-'}
                  </p>
                  <p>
                    <span className="font-medium">{t('anonymousUpload.fields.taxAmount')}:</span>{' '}
                    {analysisMetadata.taxAmount != null
                      ? `${analysisMetadata.taxAmount} ${analysisMetadata.currency || ''}`
                      : '-'}
                  </p>
                  <p><span className="font-medium">{t('anonymousUpload.fields.confidence')}:</span> {analysisMetadata.confidence != null ? analysisMetadata.confidence.toFixed(2) : '-'}</p>
                </div>

                {temporaryXmlUrl && temporaryXmlId && (
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-slate-500">{t('anonymousUpload.temporaryXmlNotice')}</p>
                    <button
                      onClick={handleDownloadTemporaryXml}
                      className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      {t('anonymousUpload.downloadXml')}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-4">
                {t('anonymousUpload.registerPrompt')}
              </p>
              <Link
                to="/register"
                className="inline-flex items-center rounded-lg bg-green-600 px-6 py-3 font-medium text-white transition-colors hover:bg-green-700"
              >
                {t('navigation.register')}
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AnonymousUpload; 